import {
  RoutineType,
  SingleStateConfig,
  WorkflowRoutineConfig,
  WorkspaceAsset,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GRIPP_LOGO_ASPECT,
  grippLogoDataUri,
} from '../../shared/components/grippLogo';
import { PRINT_SHEET_CLASS as C, printSheetCss } from './printStyles';
import { RoutinePrintBody } from './questions/routinePrintQuestionModules';

/**
 * Built once: the logo is the same on every sheet, and inlining it as a data
 * URI keeps the printed iframe from having to fetch anything.
 */
const LOGO_URI = grippLogoDataUri();

/** Sized off the footer's own line height so the band stays one line tall. */
const FOOTER_LOGO_HEIGHT = 14;
const FOOTER_LOGO_WIDTH = Math.round(FOOTER_LOGO_HEIGHT * GRIPP_LOGO_ASPECT);

type RoutinePrintSheetProps = {
  routine: WorkspaceRoutine;
  asset?: WorkspaceAsset | null;
};

const hasRequiredQuestion = (routine: WorkspaceRoutine): boolean => {
  if (routine.type !== RoutineType.Workflow) return false;
  const config = routine.config as WorkflowRoutineConfig;
  return (config?.pages ?? []).some((page) =>
    (page.questions ?? []).some((question) => question.required)
  );
};

/**
 * A blank paper form for one routine: header, the routine's own fields, and a
 * footer repeated on every printed page. Built from plain DOM rather than
 * Crop Circle components because print layout needs `break-inside`, `@page` and
 * physical units, none of which react-native-web models -- see `printStyles.ts`.
 */
export const RoutinePrintSheet = forwardRef<HTMLDivElement, RoutinePrintSheetProps>(
  ({ routine, asset }, ref) => {
    const { t } = useTranslation();
    const config = routine.config as Partial<SingleStateConfig>;
    const title = config?.title || routine.name || '';
    // Only the single-state types carry routine-level instructions as a rule;
    // a Workflow keeps its wording on the pages and questions.
    const instructions = config?.instructions;

    return (
      <div className={C} ref={ref}>
        <style>{printSheetCss}</style>

        <header className={`${C}__header`}>
          <h1 className={`${C}__title`}>{title}</h1>
          {instructions?.trim() ? (
            <p className={`${C}__routineInstructions`}>{instructions}</p>
          ) : null}

          {/*
            * Always emitted, revealed two ways. The root's own required
            * questions are known here, but an expanded nested routine's are
            * not -- those resolve inside async children, long after this
            * header renders -- so `:has()` reveals the legend from the marker
            * actually being on the sheet. The class keeps the root case
            * working where `:has()` is unsupported.
            */}
          <p
            className={`${C}__legend${
              hasRequiredQuestion(routine) ? ` ${C}__legend--shown` : ''
            }`}
          >
            {`* ${t('routine.print.required', 'required')}`}
          </p>
        </header>

        <RoutinePrintBody routine={routine} depth={0} />

        <footer className={`${C}__footer`}>
          <span className={`${C}__footerTitle`}>{title}</span>
          {/* An `<img>`, not inline SVG: Chrome paints inline SVG inside a
              repeated print footer on the first page only. */}
          <img
            className={`${C}__logo`}
            src={LOGO_URI}
            alt="Gripp"
            width={FOOTER_LOGO_WIDTH}
            height={FOOTER_LOGO_HEIGHT}
          />
          <span className={`${C}__footerAsset`}>{asset?.asset?.name}</span>
        </footer>
      </div>
    );
  }
);

RoutinePrintSheet.displayName = 'RoutinePrintSheet';
